import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Sparkles, Trash2, Repeat, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/AppLayout";
import { api, formatErr, usePoll } from "@/lib/api";
import { toast } from "sonner";

const TYPES = { license_renewal: ["License renewal", "bg-emerald-50 text-emerald-700 border-emerald-200"], annual_filing: ["Annual filing", "bg-blue-50 text-blue-700 border-blue-200"],
  grant_report: ["Grant report", "bg-amber-50 text-amber-700 border-amber-200"], grant_deadline: ["Grant deadline", "bg-rose-50 text-rose-700 border-rose-200"], lease_milestone: ["Lease milestone", "bg-sky-50 text-sky-700 border-sky-200"], tax: ["Tax", "bg-purple-50 text-purple-700 border-purple-200"], custom: ["Other", "bg-slate-50 text-slate-600 border-slate-200"] };
const REC = { none: "One-time", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

function daysUntil(d) {
  const ms = new Date(d + "T00:00:00") - new Date(new Date().toDateString());
  return Math.round(ms / 86400000);
}

function AddEvent({ onAdded }) {
  const [f, setF] = useState({ title: "", date: "", type: "custom", recurrence: "none", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/calendar", f); setF({ title: "", date: "", type: "custom", recurrence: "none", notes: "" }); onAdded(); toast.success("Deadline added"); }
    catch (e2) { toast.error(formatErr(e2)); }
  };
  const sel = (k, map, tid) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
      <SelectTrigger className="mt-1.5" data-testid={tid}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(map).map(([v, l]) => <SelectItem key={v} value={v}>{Array.isArray(l) ? l[0] : l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="calendar-add-form">
      <div className="md:col-span-4"><Label>Deadline</Label><Input required className="mt-1.5" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. State annual report" data-testid="calendar-title-input" /></div>
      <div className="md:col-span-2"><Label>Date</Label><Input required type="date" className="mt-1.5" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="calendar-date-input" /></div>
      <div className="md:col-span-2"><Label>Type</Label>{sel("type", TYPES, "calendar-type-select")}</div>
      <div className="md:col-span-2"><Label>Repeats</Label>{sel("recurrence", REC, "calendar-recurrence-select")}</div>
      <div className="md:col-span-2 flex items-end"><Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="calendar-add-submit-button"><Plus className="h-4 w-4 mr-1" />Add</Button></div>
    </form>
  );
}

export default function CalendarPage() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/calendar").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  usePoll(load, data?.autopopulate_status === "pending", 4000);
  const auto = async () => { await api.post("/calendar/autopopulate"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this deadline?")) return; await api.delete(`/calendar/${id}`); load(); };
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />;
  const groups = data.events.reduce((acc, e) => {
    const k = new Date(e.next_date + "T00:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" });
    (acc[k] = acc[k] || []).push(e);
    return acc;
  }, {});
  return (
    <div>
      <PageHeader eyebrow="Compliance Calendar" title="Never miss a deadline"
        action={<Button onClick={auto} disabled={data.autopopulate_status === "pending"} className="bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="calendar-autopopulate-button">
          {data.autopopulate_status === "pending" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Auto-populate with AI</Button>}>
        License renewals, annual filings and grant progress reports. The workspace owner gets an email reminder 7 days before each deadline.
      </PageHeader>
      {data.autopopulate_status === "pending" && <p className="text-sm text-emerald-700 mb-4" data-testid="calendar-autopopulate-pending">Researching deadlines from your business profile and licensing guides…</p>}
      {data.autopopulate_status === "error" && <p className="text-sm text-red-600 mb-4">Auto-populate failed. Please try again.</p>}
      <AddEvent onAdded={load} />
      {data.events.length === 0 ? <p className="text-slate-500 mt-10" data-testid="calendar-empty">No deadlines yet. Add one or auto-populate with AI.</p> : (
        <div className="mt-10 space-y-8" data-testid="calendar-events-list">
          {Object.entries(groups).map(([month, evs]) => (
            <section key={month}>
              <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">{month}</h3>
              <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                {evs.map((e) => {
                  const d = daysUntil(e.next_date);
                  const [label, cls] = TYPES[e.type] || TYPES.custom;
                  return (
                    <div key={e.event_id} className="p-4 flex items-center gap-4" data-testid={`calendar-event-${e.event_id}`}>
                      <div className="w-14 text-center shrink-0">
                        <div className="font-display text-2xl font-extrabold text-slate-900 leading-none">{new Date(e.next_date + "T00:00:00").getDate()}</div>
                        <div className="text-[10px] font-mono uppercase text-slate-400">{new Date(e.next_date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" })}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-sm">{e.title}</div>
                        {e.notes && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{e.notes}</div>}
                        <div className="flex gap-2 mt-1.5 items-center flex-wrap">
                          <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${cls}`}>{label}</span>
                          {e.recurrence !== "none" && <span className="text-xs text-slate-500 flex items-center gap-1"><Repeat className="h-3 w-3" />{REC[e.recurrence]}</span>}
                          {e.reminded_for === e.next_date && <span className="text-xs text-slate-500 flex items-center gap-1"><Bell className="h-3 w-3" />Reminder sent</span>}
                        </div>
                      </div>
                      <span className={`text-xs font-semibold shrink-0 ${d <= 7 ? "text-red-600" : d <= 30 ? "text-amber-600" : "text-slate-500"}`}>{d === 0 ? "Today" : d < 0 ? "Past" : `in ${d}d`}</span>
                      <button onClick={() => del(e.event_id)} className="p-1.5 text-slate-300 hover:text-red-600" data-testid={`calendar-delete-${e.event_id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
